import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Megaphone, Sparkles, Clock, MapPin, Plus, Send, Lock, CheckCircle2, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function timeLeft(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3_600_000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h left`;
  if (h >= 1) return `${h}h left`;
  return `${Math.max(1, Math.floor(ms / 60000))}m left`;
}

function CreateInquiryDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [isGlobal, setIsGlobal] = useState(false);
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [cities, setCities] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/cities").then((r) => setCities(r.data || []));
    api.get("/categories").then((r) => setCats(r.data || []));
  }, [open]);

  const canSubmit = text.trim().length >= 10 && (isGlobal || (city && category));

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) {
      toast.error("Please describe your need in at least 10 characters");
      return;
    }
    if (!isGlobal && (!city || !category)) {
      toast.error("Pick a city AND category — or switch on 'Float globally' to skip both.");
      return;
    }
    const expiresAt = new Date(Date.now() + hours * 3600 * 1000).toISOString();
    setBusy(true);
    try {
      const { data } = await api.post("/inquiries", {
        text: text.trim(),
        city: isGlobal ? null : city,
        category: isGlobal ? null : category,
        is_global: isGlobal,
        expires_at: expiresAt,
      });
      toast.success(isGlobal ? "Inquiry floated community-wide" : "Inquiry floated to matching vendors");
      setOpen(false);
      setText("");
      onCreated?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create inquiry";
      toast.error(String(msg).replace(/_/g, " "));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full" data-testid="inq-create-btn">
          <Plus className="h-4 w-4 mr-1" /> Float a request
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Float a community request</DialogTitle>
          <DialogDescription>
            Describe what you need. Interested vendors reply privately — only you see their prices.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Textarea
            data-testid="inq-create-text"
            rows={5}
            placeholder="E.g. I need tiffin services in Nagpur, Pratap Nagar area for 1 month. Interested vendors please reach out with prices. Please don't PM, just give your prices."
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Select value={city || "any"} onValueChange={(v) => setCity(v === "any" ? "" : v)} disabled={isGlobal}>
              <SelectTrigger data-testid="inq-create-city"><SelectValue placeholder={isGlobal ? "— any city —" : "Pick a city *"} /></SelectTrigger>
              <SelectContent>
                {cities.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={category || "any"} onValueChange={(v) => setCategory(v === "any" ? "" : v)} disabled={isGlobal}>
              <SelectTrigger data-testid="inq-create-category"><SelectValue placeholder={isGlobal ? "— any category —" : "Pick a category *"} /></SelectTrigger>
              <SelectContent>
                {cats.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 p-3 cursor-pointer" data-testid="inq-create-global-toggle">
            <input
              type="checkbox"
              checked={isGlobal}
              onChange={(e) => setIsGlobal(e.target.checked)}
              className="mt-0.5 h-4 w-4"
            />
            <span className="text-xs leading-relaxed">
              <strong>Float globally</strong> — broadcast to every community vendor (all cities, all categories).
              Use this only if you truly can&apos;t narrow it down; otherwise pick a city + category above so the right vendors see it first.
            </span>
          </label>
          <div>
            <label className="text-xs text-muted-foreground">Auto-close after</label>
            <Select value={String(hours)} onValueChange={(v) => setHours(Number(v))}>
              <SelectTrigger data-testid="inq-create-expiry"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="6">6 hours</SelectItem>
                <SelectItem value="12">12 hours</SelectItem>
                <SelectItem value="24">1 day</SelectItem>
                <SelectItem value="48">2 days</SelectItem>
                <SelectItem value="72">3 days (max)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">Maximum 3 days from now. Vendor replies stay private to you.</p>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy || !canSubmit} className="rounded-full" data-testid="inq-create-submit">
              {busy ? "Posting…" : (isGlobal ? "Post globally" : "Post inquiry")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReplyForm({ inquiryId, existing, onSaved }) {
  const [text, setText] = useState(existing?.text || "");
  const [price, setPrice] = useState(existing?.price || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/inquiries/${inquiryId}/replies`, {
        text: text.trim(),
        price: price.trim() || null,
      });
      toast.success(existing ? "Reply updated" : "Reply submitted");
      onSaved?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit reply";
      toast.error(String(msg).replace(/_/g, " "));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 mt-3" data-testid={`inq-reply-form-${inquiryId}`}>
      <Input
        placeholder="Your price (e.g. ₹3,500/mo)"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        className="sm:w-44"
        data-testid={`inq-reply-price-${inquiryId}`}
      />
      <Input
        placeholder="Short reply — only the inquirer sees this"
        value={text}
        onChange={(e) => setText(e.target.value)}
        required
        className="flex-1"
        data-testid={`inq-reply-text-${inquiryId}`}
      />
      <Button type="submit" disabled={busy} className="rounded-full" data-testid={`inq-reply-submit-${inquiryId}`}>
        <Send className="h-4 w-4 mr-1" /> {existing ? "Update" : "Reply"}
      </Button>
    </form>
  );
}

export default function InquiryFeed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCity, setFilterCity] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [cities, setCities] = useState([]);
  const [cats, setCats] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCity) params.city = filterCity;
      if (filterCategory) params.category = filterCategory;
      const { data } = await api.get("/inquiries/feed", { params });
      setItems(data?.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCity, filterCategory]);
  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data || []));
    api.get("/categories").then((r) => setCats(r.data || []));
  }, []);

  const mine = useMemo(() => items.filter((i) => i.is_mine), [items]);
  const others = useMemo(() => items.filter((i) => !i.is_mine), [items]);

  return (
    <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <Megaphone className="h-3 w-3" /> Community Inquiries
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter mt-1">Float a need · Get prices back</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Post a short request — interested vendors quote you directly. Replies stay private to you; vendors never see each other&apos;s prices.
          </p>
        </div>
        <CreateInquiryDialog onCreated={load} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6 max-w-xl" data-testid="inq-filters">
        <Select value={filterCity || "any"} onValueChange={(v) => setFilterCity(v === "any" ? "" : v)}>
          <SelectTrigger data-testid="inq-filter-city"><SelectValue placeholder="Filter by city" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">All cities</SelectItem>
            {cities.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCategory || "any"} onValueChange={(v) => setFilterCategory(v === "any" ? "" : v)}>
          <SelectTrigger data-testid="inq-filter-category"><SelectValue placeholder="Filter by category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {mine.length > 0 && (
        <section className="mb-8" data-testid="inq-mine-section">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Your active inquiries</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {mine.map((i) => (
              <Card key={i.inquiry_id} className="p-4 border-purple-200/60 bg-purple-50/40 dark:bg-purple-950/10" data-testid={`inq-mine-${i.inquiry_id}`}>
                <p className="text-sm line-clamp-3">{i.text}</p>
                <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-3 flex-wrap">
                  {i.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {i.city}</span>}
                  {i.category && <Badge variant="secondary" className="rounded-full text-[10px]">{i.category}</Badge>}
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {timeLeft(i.expires_at)}</span>
                  <Badge className="bg-purple-600 text-white border-0">{i.reply_count} replies</Badge>
                </div>
                <Button asChild size="sm" className="rounded-full mt-3" data-testid={`inq-mine-open-${i.inquiry_id}`}>
                  <Link to={`/inquiries/${i.inquiry_id}`}><ExternalLink className="h-3 w-3 mr-1" /> Open thread</Link>
                </Button>
              </Card>
            ))}
          </div>
        </section>
      )}

      <section data-testid="inq-feed-section">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-2">
          Latest community inquiries <Badge variant="outline" className="rounded-full text-[10px]">{others.length}/50</Badge>
        </div>
        {loading ? (
          <Card className="p-10 border-border/60 text-center text-muted-foreground" data-testid="inq-feed-loading">Loading…</Card>
        ) : others.length === 0 ? (
          <Card className="p-10 border-border/60 text-center" data-testid="inq-feed-empty">
            <Sparkles className="h-6 w-6 text-purple-500 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No active inquiries right now. Be the first to float a need.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {others.map((i) => (
              <Card key={i.inquiry_id} className="p-4 border-border/60" data-testid={`inq-feed-card-${i.inquiry_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm whitespace-pre-wrap">{i.text}</p>
                    <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-3 flex-wrap">
                      <span>— {i.inquirer_name}</span>
                      {i.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {i.city}</span>}
                      {i.category && <Badge variant="secondary" className="rounded-full text-[10px]">{i.category}</Badge>}
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {timeLeft(i.expires_at)}</span>
                    </div>
                  </div>
                  <Badge className="bg-muted text-muted-foreground border-0 gap-1" data-testid={`inq-feed-locked-${i.inquiry_id}`}>
                    <Lock className="h-3 w-3" /> Replies private
                  </Badge>
                </div>
                {i.my_reply ? (
                  <>
                    <div className="mt-3 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200/60 text-xs flex items-center gap-2" data-testid={`inq-feed-my-reply-${i.inquiry_id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span className="font-medium">Your reply{i.my_reply.price ? ` · ${i.my_reply.price}` : ""}:</span>
                      <span className="truncate text-emerald-900 dark:text-emerald-200">{i.my_reply.text}</span>
                    </div>
                    <ReplyForm inquiryId={i.inquiry_id} existing={i.my_reply} onSaved={load} />
                  </>
                ) : (
                  <ReplyForm inquiryId={i.inquiry_id} onSaved={load} />
                )}
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
